import { Divider, Typography, useMediaQuery, ArrowRightIcon } from '@ag1/ui';
import { useUpcomingOrder } from '@portal/shared/hooks/useUpcomingOrder';
import { useHeaderContext } from '@portal/state/context/HeaderContext';
import clsx from 'clsx';
import { format } from 'date-fns';
import { type UpcomingOrderStatus } from 'shared/constants/orders';
import { useLocale } from 'shared/hooks/useLocale';

import { getDateLocale } from 'shared/utils/date';
import { getShortLocale } from 'shared/utils/locale';
import HeaderMenuLink from '../LinkElement/LinkElement';
import styles from './HeaderAccountDropdown.module.scss';

interface OrderTagProps {
  isNextOrder: boolean;
  status: UpcomingOrderStatus | '';
  date: Date;
  isMobile: boolean;
}

const OrderTag = ({ isNextOrder, status, date, isMobile }: OrderTagProps) => {
  const locale = useLocale();
  const shortLocale = getShortLocale(locale);
  const dateLocale = getDateLocale(shortLocale);
  const formattedDate = format(date, 'MMM d', { locale: dateLocale });
  const { headerData } = useHeaderContext();
  const label = isNextOrder
    ? headerData.header?.nextOrderText || 'Next Order'
    : headerData.header?.currentOrderText || 'Current Order';

  let displayText: string = status;
  if (isNextOrder) {
    displayText = formattedDate;
  } else if (status === 'Delivered') {
    displayText = `${status}, ${formattedDate}`;
  }

  if (isMobile) {
    return (
      <div className={styles.accountOrderStatusCombined}>
        <Typography variant="portalText" size="1" className={styles.orderLabelText}>
          {`${label.toLocaleLowerCase()}: ${displayText}`}
        </Typography>
        <ArrowRightIcon />
      </div>
    );
  }

  return (
    <>
      <Typography variant="captionMono" size="1" className={styles.orderLabelText}>
        {label}
      </Typography>
      <div className={styles.orderStatusDate}>
        <Typography variant="buttonUnderline" size="medium" className={styles.orderDateText}>
          {displayText}
        </Typography>
        <ArrowRightIcon />
      </div>
    </>
  );
};

const HeaderOrderStatus = ({ handleClick }: { handleClick?: () => void }) => {
  const isMobile = !useMediaQuery('m');
  const upcomingOrder = useUpcomingOrder();

  if (!upcomingOrder) return null;

  const { status, date, path, isNextOrder } = upcomingOrder;

  if (!date || !path) {
    return null;
  }

  const orderDate: Date = date;

  return (
    <>
      <HeaderMenuLink
        className={clsx(styles.accountDropdownTab, styles.accountOrderStatus)}
        href={path}
        onClick={handleClick}
        target={path.includes('aftership') ? '_blank' : undefined}>
        <OrderTag isNextOrder={isNextOrder} status={status} date={orderDate} isMobile={isMobile} />
      </HeaderMenuLink>
      {!isMobile && <Divider classNameContainer={styles.accountDropdownDivider} />}
    </>
  );
};

export default HeaderOrderStatus;
